import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Message,
  Modal,
  Radio,
  Space,
  Table,
  Tabs,
  Tag,
} from '@arco-design/web-react';
import { IconCopy, IconPlus } from '@arco-design/web-react/icon';
import { PageHeader, MoneyInput, StatusPill } from '@/components/ui';
import { teamApi } from '@/api';
import { useAuthStore } from '@/store/authStore';
import { formatMoney, dollarsToCents, formatPercent, centsToDollars } from '@/utils/money';
import { requireStepUpToken } from '@/utils/secondaryAuth';
import type { ChildSummary, InvitationItem, ParentWalletTransfer, TransferDirection } from '@/types/team';

const TabPane = Tabs.TabPane;

function copyText(text: string) {
  void navigator.clipboard.writeText(text);
  Message.success('已复制到剪贴板');
}

export default function TeamView() {
  const [activeTab, setActiveTab] = useState('children');

  return (
    <div className="up-page">
      <PageHeader title="团队管理" subtitle="管理直接下级、邀请码与资金划拨" />
      <Tabs activeTab={activeTab} onChange={setActiveTab} type="rounded">
        <TabPane key="children" title="下级列表">
          <ChildrenTab />
        </TabPane>
        <TabPane key="invitations" title="邀请管理">
          <InvitationsTab />
        </TabPane>
        <TabPane key="transfers" title="划拨记录">
          <TransfersTab />
        </TabPane>
      </Tabs>
    </div>
  );
}

function ChildrenTab() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ChildSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [transferTarget, setTransferTarget] = useState<ChildSummary | null>(null);
  const [feesTarget, setFeesTarget] = useState<ChildSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teamApi.listChildren({ keyword: keyword || undefined, page, pageSize: 20 });
      setItems(res.items);
      setTotal(res.total);
    } catch {
      /* handled by interceptor */
    } finally {
      setLoading(false);
    }
  }, [keyword, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleEnabled = async (child: ChildSummary) => {
    const enabling = !child.enabled;
    const label = enabling ? '启用' : '禁用';
    Modal.confirm({
      title: `确认${label}下级`,
      content: `确定要${label}客户 ${child.username} 吗？`,
      onOk: async () => {
        const token = await requireStepUpToken('parent_disable_child', `${label}下级账号`);
        await teamApi.updateChild(child.id, { enabled: enabling }, token);
        Message.success(`已${label}`);
        void load();
      },
    });
  };

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="按客户编号搜索"
          allowClear
          style={{ width: 280 }}
          onSearch={(v) => {
            setKeyword(v);
            setPage(1);
          }}
        />
      </Space>

      {items.length === 0 && !loading ? (
        <Alert type="info" content="暂无下级客户，请前往「邀请管理」生成邀请链接发展下级" />
      ) : null}

      <Table
        rowKey="id"
        loading={loading}
        data={items}
        pagination={{
          current: page,
          pageSize: 20,
          total,
          onChange: (p) => setPage(p),
        }}
        columns={[
          { title: '客户编号', dataIndex: 'username' },
          { title: '邮箱', dataIndex: 'email' },
          {
            title: '主账户余额',
            render: (_, r) => formatMoney(r.walletBalanceCents),
          },
          { title: '下级人数', dataIndex: 'directChildCount' },
          {
            title: '入账费率',
            render: (_, r) => formatPercent(r.depositFeeRateBps ?? 0),
          },
          {
            title: '开卡费',
            render: (_, r) => formatMoney(r.cardOpenFeeCents ?? 0),
          },
          {
            title: '状态',
            render: (_, r) => (
              <Space>
                <StatusPill tone={r.enabled ? 'success' : 'danger'}>{r.enabled ? '启用' : '禁用'}</StatusPill>
                {!r.enabled && (
                  <Tag size="small" color={r.disabledByParent ? 'orange' : 'red'}>
                    {r.disabledByParent ? '上级禁用' : '管理员禁用'}
                  </Tag>
                )}
              </Space>
            ),
          },
          {
            title: '注册时间',
            dataIndex: 'createdAt',
            render: (v: string) => new Date(v).toLocaleString(),
          },
          {
            title: '操作',
            render: (_, r) => (
              <Space>
                <Button size="mini" type="primary" onClick={() => setTransferTarget(r)}>
                  划拨资金
                </Button>
                <Button size="mini" onClick={() => setFeesTarget(r)}>
                  设定费率
                </Button>
                <Button size="mini" status={r.enabled ? 'warning' : 'success'} onClick={() => void toggleEnabled(r)}>
                  {r.enabled ? '禁用' : '启用'}
                </Button>
              </Space>
            ),
          },
        ]}
      />

      {transferTarget ? (
        <TransferModal child={transferTarget} onClose={() => setTransferTarget(null)} onDone={() => void load()} />
      ) : null}
      {feesTarget ? (
        <SetFeesModal child={feesTarget} onClose={() => setFeesTarget(null)} onDone={() => void load()} />
      ) : null}
    </>
  );
}

function SetFeesModal({
  child,
  onClose,
  onDone,
}: {
  child: ChildSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const profile = useAuthStore((s) => s.profile);
  const minRate = profile?.depositFeeRateBps ?? 0;
  const minOpen = profile?.cardOpenFeeCents ?? 0;
  const [form] = Form.useForm<{ depositFeeRateBps: number; cardOpenFeeCents: number }>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      depositFeeRateBps: child.depositFeeRateBps ?? minRate,
      cardOpenFeeCents: centsToDollars(child.cardOpenFeeCents ?? minOpen),
    });
  }, [child, form, minOpen, minRate]);

  const submit = async () => {
    try {
      const v = await form.validate();
      if (v.depositFeeRateBps < minRate) {
        Message.error(`入账费率不得低于 ${formatPercent(minRate)}`);
        return;
      }
      const openCents = dollarsToCents(v.cardOpenFeeCents);
      if (openCents < minOpen) {
        Message.error(`开卡费不得低于 ${formatMoney(minOpen)}`);
        return;
      }
      setLoading(true);
      const token = await requireStepUpToken('parent_update_child_fees', '设定下级费率');
      await teamApi.updateChildFees(
        child.id,
        { depositFeeRateBps: v.depositFeeRateBps, cardOpenFeeCents: openCents },
        token,
      );
      Message.success('费率已更新');
      onDone();
      onClose();
    } catch (e) {
      if (e instanceof Error && e.message === '用户取消') return;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible
      title={`设定费率 · ${child.username}`}
      onCancel={onClose}
      onOk={() => void submit()}
      confirmLoading={loading}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        content={`您的费率下限：入账 ${formatPercent(minRate)}，开卡费 ${formatMoney(minOpen)}`}
      />
      <Form form={form} layout="vertical">
        <Form.Item
          label="入账手续费率（bps，100 = 1%）"
          field="depositFeeRateBps"
          rules={[{ required: true }]}
        >
          <InputNumber min={minRate} max={10000} precision={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="开卡手续费（USD）" field="cardOpenFeeCents" rules={[{ required: true }]}>
          <MoneyInput minDollar={minOpen / 100} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function TransferModal({
  child,
  onClose,
  onDone,
}: {
  child: ChildSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const refreshProfile = useAuthStore((s) => s.refreshProfile);
  const [form] = Form.useForm<{ direction: TransferDirection; amount: number; reason: string }>();
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    try {
      const v = await form.validate();
      if (!v.reason || v.reason.length < 4) {
        Message.error('原因至少 4 个字符');
        return;
      }
      setLoading(true);
      const token = await requireStepUpToken('parent_wallet_transfer', '向下级划拨资金');
      await teamApi.createWalletTransfer({
        childUserId: child.id,
        direction: v.direction,
        amountCents: dollarsToCents(v.amount),
        reason: v.reason,
        stepUpToken: token,
      });
      Message.success('划拨成功');
      await refreshProfile();
      onDone();
      onClose();
    } catch (e) {
      if (e instanceof Error && e.message === '用户取消') return;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible
      title={`划拨资金 · ${child.username}`}
      onCancel={onClose}
      onOk={() => void submit()}
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical" initialValues={{ direction: 'CREDIT' as TransferDirection }}>
        <Form.Item label="方向" field="direction" rules={[{ required: true }]}>
          <Radio.Group>
            <Radio value="CREDIT">下发（CREDIT）</Radio>
            <Radio value="DEBIT">回收（DEBIT）</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="金额（USD）" field="amount" rules={[{ required: true, message: '请输入金额' }]}>
          <MoneyInput minDollar={0.01} />
        </Form.Item>
        <Form.Item label="原因" field="reason" rules={[{ required: true, message: '请输入原因' }]}>
          <Input.TextArea placeholder="至少 4 个字符" maxLength={255} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function InvitationsTab() {
  const profile = useAuthStore((s) => s.profile);
  const minRate = profile?.depositFeeRateBps ?? 0;
  const minOpen = profile?.cardOpenFeeCents ?? 0;
  const defaultRate =
    profile?.defaultDownstreamDepositFeeRateBps ?? profile?.depositFeeRateBps ?? 0;
  const defaultOpen =
    profile?.defaultDownstreamCardOpenFeeCents ?? profile?.cardOpenFeeCents ?? 0;

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<InvitationItem[]>([]);
  const [createVisible, setCreateVisible] = useState(false);
  const [editing, setEditing] = useState<InvitationItem | null>(null);
  const [form] = Form.useForm<{
    label?: string;
    maxUses?: number;
    expiresAt?: string;
    depositFeeRateBps: number;
    cardOpenFeeCents: number;
  }>();
  const [editForm] = Form.useForm<{ depositFeeRateBps: number; cardOpenFeeCents: number }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teamApi.listInvitations();
      setItems(res.items);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    form.setFieldsValue({
      maxUses: 1,
      depositFeeRateBps: defaultRate,
      cardOpenFeeCents: centsToDollars(defaultOpen),
    });
    setCreateVisible(true);
  };

  const create = async () => {
    try {
      const v = await form.validate();
      if (v.depositFeeRateBps < minRate) {
        Message.error(`入账费率不得低于 ${formatPercent(minRate)}`);
        return;
      }
      const openCents = dollarsToCents(v.cardOpenFeeCents);
      if (openCents < minOpen) {
        Message.error(`开卡费不得低于 ${formatMoney(minOpen)}`);
        return;
      }
      await teamApi.createInvitation({
        label: v.label,
        maxUses: v.maxUses ?? 1,
        expiresAt: v.expiresAt,
        depositFeeRateBps: v.depositFeeRateBps,
        cardOpenFeeCents: openCents,
      });
      Message.success('邀请码已创建');
      setCreateVisible(false);
      form.resetFields();
      void load();
    } catch (e) {
      if (e instanceof Error) Message.error(e.message);
    }
  };

  const openEditFees = (item: InvitationItem) => {
    editForm.setFieldsValue({
      depositFeeRateBps: item.depositFeeRateBps,
      cardOpenFeeCents: centsToDollars(item.cardOpenFeeCents),
    });
    setEditing(item);
  };

  const saveEditFees = async () => {
    if (!editing) return;
    try {
      const v = await editForm.validate();
      if (v.depositFeeRateBps < minRate) {
        Message.error(`入账费率不得低于 ${formatPercent(minRate)}`);
        return;
      }
      const openCents = dollarsToCents(v.cardOpenFeeCents);
      if (openCents < minOpen) {
        Message.error(`开卡费不得低于 ${formatMoney(minOpen)}`);
        return;
      }
      await teamApi.updateInvitation(editing.id, {
        depositFeeRateBps: v.depositFeeRateBps,
        cardOpenFeeCents: openCents,
      });
      Message.success('邀请码费率已更新');
      setEditing(null);
      void load();
    } catch (e) {
      if (e instanceof Error) Message.error(e.message);
    }
  };

  const disable = (item: InvitationItem) => {
    Modal.confirm({
      title: '停用邀请码',
      content: `确定停用 ${item.code} 吗？停用后不可用于新注册。`,
      onOk: async () => {
        await teamApi.disableInvitation(item.id);
        Message.success('已停用');
        void load();
      },
    });
  };

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<IconPlus />} onClick={openCreate}>
          创建邀请码
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        data={items}
        pagination={false}
        columns={[
          { title: '邀请码', dataIndex: 'code' },
          { title: '标签', dataIndex: 'label', render: (v) => v || '—' },
          {
            title: '入账费率',
            render: (_, r) => formatPercent(r.depositFeeRateBps ?? 0),
          },
          {
            title: '开卡费',
            render: (_, r) => formatMoney(r.cardOpenFeeCents ?? 0),
          },
          {
            title: '邀请链接',
            render: (_, r) => (
              <Space>
                <span style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.inviteUrl}</span>
                <Button size="mini" icon={<IconCopy />} onClick={() => copyText(r.inviteUrl)} />
              </Space>
            ),
          },
          {
            title: '已用/上限',
            render: (_, r) => `${r.useCount} / ${r.maxUses ?? '不限'}`,
          },
          {
            title: '状态',
            render: (_, r) => (
              <StatusPill tone={r.enabled ? 'success' : 'default'}>{r.enabled ? '有效' : '已停用'}</StatusPill>
            ),
          },
          {
            title: '创建时间',
            dataIndex: 'createdAt',
            render: (v: string) => new Date(v).toLocaleString(),
          },
          {
            title: '操作',
            render: (_, r) => (
              <Space>
                {r.enabled ? (
                  <Button size="mini" onClick={() => openEditFees(r)}>
                    修改费率
                  </Button>
                ) : null}
                {r.enabled ? (
                  <Button size="mini" status="warning" onClick={() => disable(r)}>
                    停用
                  </Button>
                ) : null}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        visible={createVisible}
        title="创建邀请码"
        onCancel={() => setCreateVisible(false)}
        onOk={() => void create()}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          content={`您的费率下限：入账 ${formatPercent(minRate)}，开卡费 ${formatMoney(minOpen)}。默认值来自设置中心。`}
        />
        <Form form={form} layout="vertical">
          <Form.Item label="标签（可选）" field="label">
            <Input placeholder="如：2026 Q3 渠道" maxLength={64} />
          </Form.Item>
          <Form.Item label="使用次数上限" field="maxUses" extra="留空表示不限次数">
            <InputNumber min={1} placeholder="默认 1 次" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="过期时间（可选）" field="expiresAt">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="下游入账费率（bps，100 = 1%）"
            field="depositFeeRateBps"
            rules={[{ required: true, message: '请设定入账费率' }]}
          >
            <InputNumber min={minRate} max={10000} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="下游开卡费（USD）"
            field="cardOpenFeeCents"
            rules={[{ required: true, message: '请设定开卡费' }]}
          >
            <MoneyInput minDollar={minOpen / 100} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        visible={!!editing}
        title={editing ? `修改费率 · ${editing.code}` : '修改费率'}
        onCancel={() => setEditing(null)}
        onOk={() => void saveEditFees()}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          content={`您的费率下限：入账 ${formatPercent(minRate)}，开卡费 ${formatMoney(minOpen)}。仅影响后续通过该邀请码注册的用户。`}
        />
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="下游入账费率（bps，100 = 1%）"
            field="depositFeeRateBps"
            rules={[{ required: true, message: '请设定入账费率' }]}
          >
            <InputNumber min={minRate} max={10000} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="下游开卡费（USD）"
            field="cardOpenFeeCents"
            rules={[{ required: true, message: '请设定开卡费' }]}
          >
            <MoneyInput minDollar={minOpen / 100} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function TransfersTab() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ParentWalletTransfer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teamApi.listWalletTransfers({ page, pageSize: 20 });
      setItems(res.items);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Table
      rowKey="id"
      loading={loading}
      data={items}
      pagination={{
        current: page,
        pageSize: 20,
        total,
        onChange: (p) => setPage(p),
      }}
      columns={[
        { title: '下级', dataIndex: 'childUsername' },
        {
          title: '方向',
          dataIndex: 'direction',
          render: (v: TransferDirection) => (
            <Tag color={v === 'CREDIT' ? 'green' : 'orange'}>{v === 'CREDIT' ? '下发' : '回收'}</Tag>
          ),
        },
        {
          title: '金额',
          render: (_, r) => formatMoney(r.amountCents),
        },
        { title: '原因', dataIndex: 'reason' },
        {
          title: '时间',
          dataIndex: 'createdAt',
          render: (v: string) => new Date(v).toLocaleString(),
        },
      ]}
    />
  );
}
